/**
 * Every invoice across every customer, the state of receivables, and the recurring invoices that
 * raise some of them.
 *
 * The port of `Views/InvoicesPage.xaml` + `InvoicesViewModel`. Two rules carry over:
 *
 *   * THE STRIP IGNORES THE FILTERS. It is what is owed across the whole instance, so it still says
 *     so while the list below is narrowed to one customer's drafts. Overdue is red only while
 *     something IS overdue - a red $0.00 cries wolf.
 *   * ONE CUSTOMER FILTER NARROWS BOTH HALVES - the list and the recurring section below it.
 *
 * One difference: the two combos apply as soon as they change, like the Tickets page's, while the
 * dates and the search box wait for Apply (or Enter in the search box) - a list re-querying on every
 * keystroke is not what anyone typing an invoice number wants.
 *
 * Rows reflow rather than panning as a fixed-column sheet: the XAML's six columns could not fit a
 * phone, and a card that wraps reads better than a table you have to drag sideways.
 */
import type { Container } from 'webwin';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import { InvoiceStatus, type InvoiceDto, type InvoiceListDto } from '../contracts.gen.js';
import { money, plural } from '../format.js';
import { localMidnightUtc } from '../invoicing.js';
import { CardList } from '../ui/CardList.js';
import { dateBox } from '../ui/DateBox.js';
import { confirm } from '../ui/dialogs.js';
import { EmptyState } from '../ui/EmptyState.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  comboBox,
  field,
  fillWith,
  FONT,
  GAP,
  h1,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack,
  type CrmLabel
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';
import { invoiceCard } from './sections/invoice-card.js';
import { loadCustomerChoices, type CustomerChoices } from './sections/recurring.js';
import { recurringInvoicesSection } from './sections/recurring-invoices.js';

/** Index 3 is not a status but a narrowing of Issued. */
const STATUS_FILTERS = ['Any status', 'Draft', 'Issued', 'Overdue', 'Paid', 'Void'] as const;
const OVERDUE_INDEX = 3;
const MAX_ROWS = 250;

export function invoicesPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      let choices: CustomerChoices = { names: [], ids: [] };
      let ready = false;
      let busy = false;

      const status = new StatusLine();

      // ---- header ----
      const overdueButton = button('Show overdue', () => void showOverdue(), true);
      overdueButton.visible = false;
      // The description sits under the title row rather than beside the button: a wrapping label
      // beside a fixed-width sibling is measured at the row's full width and then narrowed, which
      // clipped its last line and left a gap below it.
      const titleRow = splitRow({ align: 'center' });
      fillWith(titleRow, h1('Invoices'));
      titleRow.controls.add(overdueButton);
      const header = stackOf(
        vstack({ spacing: GAP.xs }),
        titleRow,
        muted(
          "Every invoice across every customer, and the recurring invoices that raise them. Invoices are generated from a customer's finished tickets on their Billing page; this is where they are watched, issued and chased."
        )
      );

      // ---- receivables strip ----
      const outstanding = stat('Outstanding');
      const overdue = stat('Overdue');
      const drafts = stat('Drafts');
      const paid = stat('Paid');
      const strip = card({ visible: false });
      strip.controls.add(
        stackOf(buttonRow({ spacing: GAP.xl, lineSpacing: GAP.md, align: 'start' }), outstanding.panel, overdue.panel, drafts.panel, paid.panel)
      );

      // ---- filters ----
      const statusFilter = comboBox<string>([...STATUS_FILTERS], {
        selectedIndex: 0,
        onSelectionChanged: () => void applyIfReady()
      });
      const customerFilter = comboBox<string>(['Any customer'], {
        selectedIndex: 0,
        onSelectionChanged: () => void customerChanged()
      });
      const from = dateBox();
      const to = dateBox();
      const search = textBox({ placeholder: 'Number or customer' });
      search.on('keydown', (e: { key: string }) => {
        if (e.key === 'Enter') void refresh();
      });
      const totalLine = text('');

      const filters = card();
      filters.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          stackOf(
            buttonRow({ spacing: GAP.md, align: 'end' }),
            field('Status', statusFilter, { width: 140 }),
            field('Customer', customerFilter, { width: 220 }),
            field('Created from', from.picker, { width: 150 }),
            field('to', to.picker, { width: 150 }),
            field('Search', search, { width: 200 })
          ),
          stackOf(buttonRow(), button('Apply', () => void refresh(), true), button('Reset', () => void reset())),
          totalLine
        )
      );

      // ---- list ----
      const list = new CardList<InvoiceDto>({
        key: i => i.id,
        max: MAX_ROWS,
        build: i =>
          invoiceCard(i, {
            showCustomer: true,
            onOpen: invoice => ctx.go(`/invoices/${invoice.id}`),
            onIssue: invoice => void issue(invoice),
            onMarkPaid: invoice => void markPaid(invoice)
          })
      });
      const empty = new EmptyState(
        "No invoices match. Invoices are generated from a customer's finished tickets on their Billing page, or raised on a schedule by a recurring invoice below."
      );

      const recurring = recurringInvoicesSection({ ctx, onChanged: () => void refresh() });

      const { view, content } = scrollPage();
      content.controls.add(
        stackOf(vstack({ spacing: GAP.lg }), header, status.label, strip, filters, empty.panel, list.panel)
      );
      content.controls.add(recurring.panel);
      host.controls.add(view);

      // ---------------------------------------------------------------

      function stat(caption: string): { panel: ReturnType<typeof vstack>; amount: CrmLabel; detail: CrmLabel } {
        const amount = value('', { font: FONT.h1 });
        const detail = muted('');
        const panel = stackOf(vstack({ spacing: GAP.xs, width: 180, autoWidth: false }), muted(caption), amount, detail);
        return { panel, amount, detail };
      }

      function selectedCustomerId(): number | undefined {
        const index = customerFilter.selectedIndex;
        return index > 0 && index - 1 < choices.ids.length ? choices.ids[index - 1] : undefined;
      }

      function filterRangeLabel(): string {
        const f = from.iso();
        const t = to.iso();
        return !f && !t ? 'all time' : `created ${f || 'any time'} to ${t || 'now'}`;
      }

      function render(result: InvoiceListDto): void {
        const s = result.summary;
        outstanding.amount.text = money(s.outstandingTotal);
        outstanding.detail.text = `${s.outstandingCount} issued, unpaid`;
        overdue.amount.text = money(s.overdueTotal);
        overdue.amount.role = s.overdueCount > 0 ? 'danger' : 'text';
        overdue.detail.text = s.overdueCount === 0 ? 'nothing overdue' : `${s.overdueCount} past due date`;
        drafts.amount.text = money(s.draftTotal);
        drafts.detail.text = `${plural(s.draftCount, 'draft')} to review`;
        paid.amount.text = money(s.paidTotal);
        paid.detail.text = `${s.paidCount} paid, all time`;
        strip.visible = true;
        overdueButton.visible = s.overdueCount > 0;

        list.set(result.invoices);
        empty.update(result.invoices.length);
        const shown = list.overflow > 0 ? ` (showing the first ${result.invoices.length - list.overflow})` : '';
        totalLine.text = `${plural(result.count, 'invoice')} listed${shown} · ${money(result.filteredTotal)} · ${filterRangeLabel()}`;
        relayout(content);
      }

      async function refresh(): Promise<void> {
        const index = statusFilter.selectedIndex;
        const statusValue: InvoiceStatus | undefined =
          index === 1
            ? InvoiceStatus.Draft
            : index === 2
              ? InvoiceStatus.Issued
              : index === 4
                ? InvoiceStatus.Paid
                : index === 5
                  ? InvoiceStatus.Void
                  : undefined;
        try {
          const result = await crm.getAllInvoices(
            {
              status: statusValue,
              overdue: index === OVERDUE_INDEX,
              customerId: selectedCustomerId(),
              fromUtc: localMidnightUtc(from.iso()),
              // Exclusive end of the "to" day, so the whole day is included.
              toUtc: localMidnightUtc(to.iso(), 1),
              search: search.text
            },
            ctx.dispose.signal()
          );
          if (ctx.cancelled()) return;
          render(result);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the invoices: ' + describeError(error));
        }
      }

      async function applyIfReady(): Promise<void> {
        if (ready) await refresh();
      }

      async function customerChanged(): Promise<void> {
        if (!ready) return;
        await Promise.all([refresh(), recurring.setCustomerFilter(selectedCustomerId())]);
      }

      async function showOverdue(): Promise<void> {
        ready = false;
        statusFilter.selectedIndex = OVERDUE_INDEX;
        ready = true;
        await refresh();
      }

      async function reset(): Promise<void> {
        ready = false;
        const hadCustomer = selectedCustomerId() !== undefined;
        statusFilter.selectedIndex = 0;
        customerFilter.selectedIndex = 0;
        from.set(null);
        to.set(null);
        search.text = '';
        ready = true;
        await Promise.all([refresh(), hadCustomer ? recurring.setCustomerFilter(undefined) : Promise.resolve()]);
      }

      async function issue(i: InvoiceDto): Promise<void> {
        if (busy) return;
        const ok = await confirm(ctx.desktop, {
          title: 'Issue invoice',
          message: `Issue ${i.number} to ${i.customerName} for ${money(i.total)}? It becomes payable and can no longer be edited freely.`
        });
        if (!ok || ctx.cancelled()) return;
        await act(`Issuing ${i.number}…`, () => crm.issueInvoice(i.id), `Issued ${i.number}.`);
      }

      async function markPaid(i: InvoiceDto): Promise<void> {
        if (busy) return;
        const ok = await confirm(ctx.desktop, {
          title: 'Mark paid',
          message: `Mark ${i.number} (${i.customerName}, ${money(i.total)}) as paid today?`
        });
        if (!ok || ctx.cancelled()) return;
        await act(`Marking ${i.number} paid…`, () => crm.markInvoicePaid(i.id), `Marked ${i.number} paid.`);
      }

      async function act(working: string, call: () => Promise<unknown>, done: string): Promise<void> {
        busy = true;
        status.busy(working);
        try {
          await call();
          if (ctx.cancelled()) return;
          status.ok(done);
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function initialize(): Promise<void> {
        choices = await loadCustomerChoices(ctx.dispose.signal());
        if (ctx.cancelled()) return;
        customerFilter.items = ['Any customer', ...choices.names];
        customerFilter.selectedIndex = 0;
        if (ctx.query.get('overdue') === '1') statusFilter.selectedIndex = OVERDUE_INDEX;
        ready = true;
        await Promise.all([refresh(), recurring.initialize()]);
      }

      void initialize();
    }
  };
}
